import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';

// Helper: send order confirmation email via edge function
const sendOrderConfirmationEmail = async ({ clientEmail, companyName, orderType, orderDetails, volumeM3, estimatedCost, pickupAddress, clientNotes, needsEx1 }) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    await fetch(`${supabaseUrl}/functions/v1/send-pro-order-confirmation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({
        clientEmail,
        companyName,
        orderType,
        orderDetails,
        volumeM3,
        estimatedCost,
        pickupAddress,
        clientNotes,
        needsEx1,
        orderDate: new Date()?.toLocaleDateString('fr-FR'),
      }),
    });
  } catch (err) {
    console.warn('Email confirmation could not be sent:', err);
  }
};

// Static barrel types (2 formats: noire 270L et bleue 220L)
const BARREL_TYPES = [
  { value: 'barrique_noire_vide', label: 'Barrique noire 270L — vide', icon: 'Wine', unit: 'unité(s)', operation: 'depot', barriqueFull: false, note: 'Dépôt de barrique noire vide chez le client' },
  { value: 'barrique_noire_pleine', label: 'Barrique noire 270L — pleine', icon: 'Wine', unit: 'unité(s)', operation: 'enlevement', barriqueFull: true, note: 'Enlèvement de barrique noire pleine chez le client' },
  { value: 'barrique_bleue_vide', label: 'Barrique bleue 220L — vide', icon: 'Wine', unit: 'unité(s)', operation: 'depot', barriqueFull: false, note: 'Dépôt de barrique bleue vide chez le client' },
  { value: 'barrique_bleue_pleine', label: 'Barrique bleue 220L — pleine', icon: 'Wine', unit: 'unité(s)', operation: 'enlevement', barriqueFull: true, note: 'Enlèvement de barrique bleue pleine chez le client' },
];

// Static non-barrel, non-carton article types (Moto/Scooter removed, Meuble/Palette moved to volume)
const OTHER_ARTICLE_TYPES = [
  { value: 'frigo', label: 'Réfrigérateur / Frigo', icon: 'Thermometer', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
  { value: 'congelateur', label: 'Congélateur', icon: 'Thermometer', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
  { value: 'machine_laver', label: 'Machine à laver', icon: 'Zap', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
  { value: 'televiseur', label: 'Téléviseur / TV', icon: 'Monitor', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
  { value: 'climatiseur', label: 'Climatiseur', icon: 'Wind', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
  { value: 'electromenager', label: 'Autre électroménager', icon: 'Zap', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
  { value: 'velo', label: 'Vélo', icon: 'Bike', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
  { value: 'autre', label: 'Autre article', icon: 'MoreHorizontal', unit: 'article(s)', price: null, operation: 'enlevement', note: 'Enlèvement chez le client' },
];

const createEmptyLine = () => ({
  id: Date.now() + Math.random(),
  type: 'barrique_noire_vide',
  quantite: 1,
  description: '',
});

const Commander = ({ professionalClient }) => {
  const [activeOption, setActiveOption] = useState('commander');
  const [orderLines, setOrderLines] = useState([createEmptyLine()]);
  const [globalAddress, setGlobalAddress] = useState('');
  const [globalDate, setGlobalDate] = useState('');
  const [globalNotes, setGlobalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Carton inventory from Supabase
  const [cartonInventory, setCartonInventory] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  // Real orders from Supabase
  const [orderHistory, setOrderHistory] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  // Volume form (now includes meuble/palette)
  const [volumeForm, setVolumeForm] = useState({
    longueur: '',
    largeur: '',
    hauteur: '',
    unite: 'cm',
    description: '',
    adresseEnlevement: '',
    dateEnlevement: '',
    certificatEX1: false,
    articleType: 'dimensions', // 'dimensions' | 'meuble' | 'palette'
    nbMeubles: 1,
    nbPalettes: 1,
  });

  // Conteneur form
  const [conteneurForm, setConteneurForm] = useState({
    type: '20pieds',
    description: '',
    adresseEnlevement: '',
    dateEnlevement: '',
    certificatEX1: false
  });

  useEffect(() => {
    loadCartonInventory();
    loadOrderHistory();
  }, []);

  const loadCartonInventory = async () => {
    try {
      setLoadingCatalog(true);
      const { data, error } = await supabase
        ?.from('carton_inventory')
        ?.select('id, carton_type, dimensions, unit_price_sell')
        ?.order('carton_type', { ascending: true });
      if (error) throw error;
      setCartonInventory(data || []);
    } catch (err) {
      console.error('Error loading carton inventory:', err);
      // Fallback to default carton types if DB fails
      setCartonInventory([
        { id: 'petit', carton_type: 'Petit', dimensions: '30x20x20 cm', unit_price_sell: 3.00 },
        { id: 'moyen', carton_type: 'Moyen', dimensions: '40x30x30 cm', unit_price_sell: 4.00 },
        { id: 'grand', carton_type: 'Grand', dimensions: '60x40x40 cm', unit_price_sell: 6.00 },
        { id: 'xl', carton_type: 'XL', dimensions: '80x60x60 cm', unit_price_sell: 10.00 },
      ]);
    } finally {
      setLoadingCatalog(false);
    }
  };

  // Build full article types list dynamically from carton inventory
  const buildArticleTypes = () => {
    const cartonTypes = cartonInventory?.flatMap(c => [
      {
        value: `carton_vide_${c?.id}`,
        label: `Carton vide — ${c?.carton_type}${c?.dimensions ? ` (${c?.dimensions})` : ''}`,
        icon: 'Package',
        unit: 'carton(s)',
        price: parseFloat(c?.unit_price_sell) || null,
        operation: 'depot',
        note: 'Dépôt de carton vide chez le client',
        cartonInventoryId: c?.id,
        cartonType: c?.carton_type,
      },
      {
        value: `carton_plein_${c?.id}`,
        label: `Carton plein — ${c?.carton_type}${c?.dimensions ? ` (${c?.dimensions})` : ''}`,
        icon: 'Package',
        unit: 'carton(s)',
        price: parseFloat(c?.unit_price_sell) || null,
        operation: 'enlevement',
        note: 'Enlèvement de carton plein chez le client',
        cartonInventoryId: c?.id,
        cartonType: c?.carton_type,
      },
    ]) || [];

    return [...BARREL_TYPES, ...cartonTypes, ...OTHER_ARTICLE_TYPES];
  };

  const ARTICLE_TYPES = buildArticleTypes();

  const loadOrderHistory = async () => {
    try {
      setLoadingOrders(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      const { data: client } = await supabase
        ?.from('professional_clients')
        ?.select('id')
        ?.eq('auth_user_id', user?.id)
        ?.maybeSingle();

      if (!client?.id) return;

      const { data, error } = await supabase
        ?.from('pro_pending_orders')
        ?.select('*')
        ?.eq('professional_client_id', client?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setOrderHistory(data || []);
    } catch (err) {
      console.error('Error loading order history:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const addLine = () => {
    setOrderLines(prev => [...prev, createEmptyLine()]);
  };

  const removeLine = (id) => {
    if (orderLines?.length === 1) return;
    setOrderLines(prev => prev?.filter(l => l?.id !== id));
  };

  const updateLine = (id, field, value) => {
    setOrderLines(prev => prev?.map(l => l?.id === id ? { ...l, [field]: value } : l));
  };

  const getArticleInfo = (typeValue) => ARTICLE_TYPES?.find(a => a?.value === typeValue) || ARTICLE_TYPES?.[0];

  const isBarrelOrCarton = (typeValue) => {
    return typeValue?.startsWith('barrique_') || typeValue?.startsWith('carton_');
  };

  const computeTotal = () => {
    return orderLines?.reduce((sum, line) => {
      const info = getArticleInfo(line?.type);
      if (info?.price) return sum + (info?.price * (parseInt(line?.quantite) || 0));
      return sum;
    }, 0);
  };

  const buildSummary = () => {
    return orderLines?.map(line => {
      const info = getArticleInfo(line?.type);
      const desc = line?.description ? ` (${line?.description})` : '';
      return `${line?.quantite} × ${info?.label}${desc}`;
    })?.join(', ');
  };

  const handleCommanderSubmit = async (e) => {
    e?.preventDefault();
    if (!globalAddress?.trim()) {
      toast?.error('Veuillez indiquer une adresse de livraison / enlèvement');
      return;
    }
    try {
      setSubmitting(true);

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Non authentifié');

      const { data: client } = await supabase
        ?.from('professional_clients')
        ?.select('id, company_name, email')
        ?.eq('auth_user_id', user?.id)
        ?.maybeSingle();

      if (!client?.id) throw new Error('Profil client introuvable');

      const total = computeTotal();
      const summary = buildSummary();

      const { error } = await supabase?.from('pro_pending_orders')?.insert({
        professional_client_id: client?.id,
        order_type: 'order',
        volume_quantity: summary,
        pickup_address: globalAddress,
        client_notes: globalNotes || null,
        estimated_amount: total > 0 ? total : null,
        status: 'new',
      });

      if (error) throw error;

      await sendOrderConfirmationEmail({
        clientEmail: client?.email || user?.email,
        companyName: client?.company_name || user?.email,
        orderType: 'order',
        orderDetails: summary,
        estimatedCost: total > 0 ? total : null,
        pickupAddress: globalAddress,
        clientNotes: globalNotes || null,
        needsEx1: false,
      });

      toast?.success('Commande envoyée avec succès !');
      setOrderLines([createEmptyLine()]);
      setGlobalAddress('');
      setGlobalDate('');
      setGlobalNotes('');
      await loadOrderHistory();
    } catch (err) {
      console.error('Order submit error:', err);
      toast?.error('Erreur lors de l\'envoi de la commande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVolumeSubmit = async (e) => {
    e?.preventDefault();

    let volumeM3 = 0;
    let volumeDetails = '';

    if (volumeForm?.articleType === 'dimensions') {
      const l = parseFloat(volumeForm?.longueur) || 0;
      const w = parseFloat(volumeForm?.largeur) || 0;
      const h = parseFloat(volumeForm?.hauteur) || 0;
      if (l <= 0 || w <= 0 || h <= 0) {
        toast?.error('Veuillez renseigner les dimensions (longueur, largeur, hauteur)');
        return;
      }
      volumeM3 = volumeForm?.unite === 'cm' ? (l * w * h) / 1000000 : l * w * h;
      volumeDetails = `${volumeM3?.toFixed(3)} m³`;
    } else if (volumeForm?.articleType === 'meuble') {
      if (!volumeForm?.nbMeubles || parseInt(volumeForm?.nbMeubles) < 1) {
        toast?.error('Veuillez indiquer le nombre de meubles');
        return;
      }
      volumeDetails = `${volumeForm?.nbMeubles} meuble(s) — volume sur devis`;
    } else if (volumeForm?.articleType === 'palette') {
      if (!volumeForm?.nbPalettes || parseInt(volumeForm?.nbPalettes) < 1) {
        toast?.error('Veuillez indiquer le nombre de palettes');
        return;
      }
      volumeDetails = `${volumeForm?.nbPalettes} palette(s) — volume sur devis`;
    }

    const tarif = volumeM3 > 0 ? volumeM3 * 450 : null;

    try {
      setSubmitting(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Non authentifié');

      const { data: client } = await supabase
        ?.from('professional_clients')
        ?.select('id, company_name, email')
        ?.eq('auth_user_id', user?.id)
        ?.maybeSingle();

      if (!client?.id) throw new Error('Profil client introuvable');

      const { error } = await supabase?.from('pro_pending_orders')?.insert({
        professional_client_id: client?.id,
        order_type: 'quote_volume',
        volume_quantity: volumeDetails,
        pickup_address: volumeForm?.adresseEnlevement || null,
        client_notes: volumeForm?.description || null,
        needs_ex1: volumeForm?.certificatEX1,
        estimated_amount: tarif || null,
        status: 'new',
      });

      if (error) throw error;

      await sendOrderConfirmationEmail({
        clientEmail: client?.email || user?.email,
        companyName: client?.company_name || user?.email,
        orderType: 'quote_volume',
        orderDetails: volumeDetails + (volumeForm?.description ? ` — ${volumeForm?.description}` : ''),
        volumeM3: volumeM3 > 0 ? volumeM3?.toFixed(3) : null,
        estimatedCost: tarif || null,
        pickupAddress: volumeForm?.adresseEnlevement || null,
        clientNotes: volumeForm?.description || null,
        needsEx1: volumeForm?.certificatEX1,
      });

      toast?.success('Demande de devis envoyée avec succès!');
      setVolumeForm({ longueur: '', largeur: '', hauteur: '', unite: 'cm', description: '', adresseEnlevement: '', dateEnlevement: '', certificatEX1: false, articleType: 'dimensions', nbMeubles: 1, nbPalettes: 1 });
      await loadOrderHistory();
    } catch (err) {
      console.error('Volume submit error:', err);
      toast?.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConteneurSubmit = async (e) => {
    e?.preventDefault();
    try {
      setSubmitting(true);
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Non authentifié');

      const { data: client } = await supabase
        ?.from('professional_clients')
        ?.select('id, company_name, email')
        ?.eq('auth_user_id', user?.id)
        ?.maybeSingle();

      if (!client?.id) throw new Error('Profil client introuvable');

      const conteneurLabel = conteneurForm?.type === '20pieds' ? 'Conteneur 20 pieds' : 'Conteneur 40 pieds';

      const { error } = await supabase?.from('pro_pending_orders')?.insert({
        professional_client_id: client?.id,
        order_type: 'quote_container',
        volume_quantity: conteneurLabel,
        pickup_address: conteneurForm?.adresseEnlevement || null,
        client_notes: conteneurForm?.description || null,
        needs_ex1: conteneurForm?.certificatEX1,
        status: 'new',
      });

      if (error) throw error;

      await sendOrderConfirmationEmail({
        clientEmail: client?.email || user?.email,
        companyName: client?.company_name || user?.email,
        orderType: 'quote_container',
        orderDetails: conteneurLabel + (conteneurForm?.description ? ` — ${conteneurForm?.description}` : ''),
        pickupAddress: conteneurForm?.adresseEnlevement || null,
        clientNotes: conteneurForm?.description || null,
        needsEx1: conteneurForm?.certificatEX1,
      });

      toast?.success('Demande de devis envoyée avec succès!');
      setConteneurForm({ type: '20pieds', description: '', adresseEnlevement: '', dateEnlevement: '', certificatEX1: false });
      await loadOrderHistory();
    } catch (err) {
      console.error('Container submit error:', err);
      toast?.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      new: { label: 'Nouvelle', color: 'bg-blue-100 text-blue-800' },
      processing: { label: 'En traitement', color: 'bg-yellow-100 text-yellow-800' },
      quote_sent: { label: 'Devis envoyé', color: 'bg-purple-100 text-purple-800' },
      confirmed: { label: 'Confirmée', color: 'bg-green-100 text-green-800' },
      rejected: { label: 'Refusée', color: 'bg-red-100 text-red-800' },
    };
    const badge = badges?.[statut] || badges?.new;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        {badge?.label}
      </span>
    );
  };

  const getOrderTypeLabel = (type) => {
    const labels = {
      order: 'Commande articles',
      quote_volume: 'Envoi au volume',
      quote_barrels: 'Barriques',
      quote_container: 'Conteneur',
    };
    return labels?.[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'd MMM yyyy', { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const handleRowClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const total = computeTotal();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Commander</h1>
      {/* Options Selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => setActiveOption('commander')}
          className={`p-6 rounded-lg border-2 transition-all text-left ${
            activeOption === 'commander' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 bg-white hover:border-green-300'
          }`}
        >
          <Icon name="ShoppingCart" size={32} className={activeOption === 'commander' ? 'text-green-600' : 'text-gray-600'} />
          <h3 className="font-bold text-gray-900 mt-3">Commander</h3>
          <p className="text-sm text-gray-600 mt-1">Barriques, cartons, enlèvements…</p>
        </button>

        <button
          onClick={() => setActiveOption('volume')}
          className={`p-6 rounded-lg border-2 transition-all text-left ${
            activeOption === 'volume' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 bg-white hover:border-green-300'
          }`}
        >
          <Icon name="Package" size={32} className={activeOption === 'volume' ? 'text-green-600' : 'text-gray-600'} />
          <h3 className="font-bold text-gray-900 mt-3">Réserver un envoi au volume</h3>
          <p className="text-sm text-gray-600 mt-1">Envoi en m³, meubles, palettes</p>
        </button>

        <button
          onClick={() => setActiveOption('conteneur')}
          className={`p-6 rounded-lg border-2 transition-all text-left ${
            activeOption === 'conteneur' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 bg-white hover:border-green-300'
          }`}
        >
          <Icon name="Container" size={32} className={activeOption === 'conteneur' ? 'text-green-600' : 'text-gray-600'} />
          <h3 className="font-bold text-gray-900 mt-3">Réserver un conteneur complet</h3>
          <p className="text-sm text-gray-600 mt-1">20 ou 40 pieds</p>
        </button>
      </div>
      {/* Forms */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">

        {/* ===== COMMANDER (multi-articles) ===== */}
        {activeOption === 'commander' && (
          <form onSubmit={handleCommanderSubmit} className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Nouvelle commande</h2>
              <span className="text-sm text-gray-500">{orderLines?.length} article{orderLines?.length > 1 ? 's' : ''}</span>
            </div>

            {loadingCatalog ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mr-2"></div>
                <span className="text-sm text-gray-500">Chargement du catalogue…</span>
              </div>
            ) : (
              <>
                {/* Order Lines */}
                <div className="space-y-3">
                  {orderLines?.map((line, index) => {
                    const info = getArticleInfo(line?.type);
                    return (
                      <div key={line?.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700">Article {index + 1}</span>
                          {orderLines?.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLine(line?.id)}
                              className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                              title="Supprimer cette ligne"
                            >
                              <Icon name="Trash2" size={16} />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="sm:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Type d'article</label>
                            <select
                              value={line?.type}
                              onChange={(e) => updateLine(line?.id, 'type', e?.target?.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 bg-white text-sm"
                            >
                              <optgroup label="— Barriques —">
                                {BARREL_TYPES?.map(a => (
                                  <option key={a?.value} value={a?.value}>{a?.label}</option>
                                ))}
                              </optgroup>
                              {cartonInventory?.length > 0 && (
                                <>
                                  <optgroup label="— Cartons vides (dépôt) —">
                                    {cartonInventory?.map(c => (
                                      <option key={`carton_vide_${c?.id}`} value={`carton_vide_${c?.id}`}>
                                        Carton vide — {c?.carton_type}{c?.dimensions ? ` (${c?.dimensions})` : ''}{c?.unit_price_sell > 0 ? ` — ${parseFloat(c?.unit_price_sell)?.toFixed(2)} €` : ''}
                                      </option>
                                    ))}
                                  </optgroup>
                                  <optgroup label="— Cartons pleins (enlèvement) —">
                                    {cartonInventory?.map(c => (
                                      <option key={`carton_plein_${c?.id}`} value={`carton_plein_${c?.id}`}>
                                        Carton plein — {c?.carton_type}{c?.dimensions ? ` (${c?.dimensions})` : ''}{c?.unit_price_sell > 0 ? ` — ${parseFloat(c?.unit_price_sell)?.toFixed(2)} €` : ''}
                                      </option>
                                    ))}
                                  </optgroup>
                                </>
                              )}
                              <optgroup label="— Autres articles —">
                                {OTHER_ARTICLE_TYPES?.map(a => (
                                  <option key={a?.value} value={a?.value}>{a?.label}</option>
                                ))}
                              </optgroup>
                            </select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Quantité</label>
                            <input
                              type="number"
                              min="1"
                              value={line?.quantite}
                              onChange={(e) => updateLine(line?.id, 'quantite', e?.target?.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                              placeholder="1"
                            />
                          </div>
                        </div>

                        {/* Operation badge */}
                        <div className="flex items-center gap-2">
                          {info?.operation === 'depot' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                              <Icon name="ArrowDownToLine" size={12} /> Dépôt chez le client
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                              <Icon name="ArrowUpFromLine" size={12} /> Enlèvement chez le client
                            </span>
                          )}
                        </div>

                        {!isBarrelOrCarton(line?.type) && (
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              {line?.type === 'autre' ? "Description de l'article" : 'Précisions (marque, modèle, dimensions…)'}
                            </label>
                            <input
                              type="text"
                              value={line?.description}
                              onChange={(e) => updateLine(line?.id, 'description', e?.target?.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                              placeholder="Ex: Samsung 300L, frigo américain…"
                            />
                          </div>
                        )}

                        {info?.price > 0 && (
                          <p className="text-xs text-green-700 font-medium">
                            {parseFloat(info?.price)?.toFixed(2)} € / unité → Sous-total : {(info?.price * (parseInt(line?.quantite) || 0))?.toFixed(2)} €
                          </p>
                        )}
                        {!info?.price && (
                          <p className="text-xs text-blue-600">Prix sur devis — notre équipe vous contactera</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={addLine}
                  className="flex items-center space-x-2 text-green-600 hover:text-green-700 font-medium text-sm border border-dashed border-green-400 rounded-lg px-4 py-2 w-full justify-center hover:bg-green-50 transition-colors"
                >
                  <Icon name="Plus" size={16} />
                  <span>Ajouter un autre article</span>
                </button>
              </>
            )}

            <div className="border-t border-gray-200 pt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse de livraison / enlèvement <span className="text-red-500">*</span>
                </label>
                <Input
                  value={globalAddress}
                  onChange={(e) => setGlobalAddress(e?.target?.value)}
                  placeholder="Adresse complète"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date souhaitée</label>
                <Input
                  type="date"
                  value={globalDate}
                  onChange={(e) => setGlobalDate(e?.target?.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes / instructions particulières</label>
                <textarea
                  value={globalNotes}
                  onChange={(e) => setGlobalNotes(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                  rows="2"
                  placeholder="Informations complémentaires pour notre équipe…"
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-1">
              <p className="text-sm text-blue-800">
                <strong>Récapitulatif :</strong> {buildSummary()}
              </p>
              {total > 0 && (
                <>
                  <p className="text-sm text-blue-800">
                    <strong>Tarif pro dégressif appliqué</strong>
                  </p>
                  <p className="text-lg font-bold text-blue-900">Total estimé : {total?.toFixed(2)} €</p>
                </>
              )}
              {total === 0 && (
                <p className="text-sm text-blue-700 italic">Certains articles seront facturés sur devis.</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={submitting || loadingCatalog}>
              {submitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Envoi en cours…</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="Send" size={18} />
                  <span>Valider la commande</span>
                </div>
              )}
            </Button>
          </form>
        )}

        {/* Volume Form — includes Meuble & Palette */}
        {activeOption === 'volume' && (
          <form onSubmit={handleVolumeSubmit} className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Réserver un envoi au volume</h2>

            {/* Article type selector */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Type d'envoi</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'dimensions', label: 'Envoi en m³', icon: 'Ruler' },
                  { key: 'meuble', label: 'Meuble(s)', icon: 'LayoutDashboard' },
                  { key: 'palette', label: 'Palette(s)', icon: 'Layers' },
                ]?.map(opt => (
                  <button
                    key={opt?.key}
                    type="button"
                    onClick={() => setVolumeForm(prev => ({ ...prev, articleType: opt?.key }))}
                    className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                      volumeForm?.articleType === opt?.key
                        ? 'border-green-500 bg-green-50 text-green-700' :'border-gray-200 bg-white text-gray-600 hover:border-green-300'
                    }`}
                  >
                    <Icon name={opt?.icon} size={20} />
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dimensions calculator */}
            {volumeForm?.articleType === 'dimensions' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-blue-800">Calculateur de volume</label>
                  <div className="flex items-center space-x-1 bg-white border border-blue-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setVolumeForm(prev => ({ ...prev, unite: 'cm' }))}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${volumeForm?.unite === 'cm' ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                    >
                      cm
                    </button>
                    <button
                      type="button"
                      onClick={() => setVolumeForm(prev => ({ ...prev, unite: 'm' }))}
                      className={`px-3 py-1 text-xs font-medium transition-colors ${volumeForm?.unite === 'm' ? 'bg-blue-600 text-white' : 'text-blue-600 hover:bg-blue-50'}`}
                    >
                      m
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Longueur ({volumeForm?.unite})</label>
                    <Input type="number" step="0.1" min="0" value={volumeForm?.longueur} onChange={(e) => setVolumeForm(prev => ({ ...prev, longueur: e?.target?.value }))} placeholder="Ex: 120" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Largeur ({volumeForm?.unite})</label>
                    <Input type="number" step="0.1" min="0" value={volumeForm?.largeur} onChange={(e) => setVolumeForm(prev => ({ ...prev, largeur: e?.target?.value }))} placeholder="Ex: 80" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Hauteur ({volumeForm?.unite})</label>
                    <Input type="number" step="0.1" min="0" value={volumeForm?.hauteur} onChange={(e) => setVolumeForm(prev => ({ ...prev, hauteur: e?.target?.value }))} placeholder="Ex: 60" />
                  </div>
                </div>

                {(() => {
                  const l = parseFloat(volumeForm?.longueur) || 0;
                  const w = parseFloat(volumeForm?.largeur) || 0;
                  const h = parseFloat(volumeForm?.hauteur) || 0;
                  let volumeM3 = 0;
                  if (l > 0 && w > 0 && h > 0) {
                    volumeM3 = volumeForm?.unite === 'cm' ? (l * w * h) / 1000000 : l * w * h;
                  }
                  const tarif = volumeM3 * 450;
                  if (volumeM3 > 0) {
                    return (
                      <div className="bg-white border border-blue-300 rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Volume calculé :</span>
                          <span className="text-lg font-bold text-blue-700">{volumeM3?.toFixed(3)} m³</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Tarif estimé (450€/m³) :</span>
                          <span className="text-lg font-bold text-green-700">{tarif?.toFixed(0)} €</span>
                        </div>
                      </div>
                    );
                  }
                  return <p className="text-xs text-blue-600 italic">Renseignez les 3 dimensions pour calculer automatiquement le volume et le tarif estimé.</p>;
                })()}

                <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <Icon name="Info" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    <span className="font-semibold">Note :</span> Tarif indicatif à 450€/m³. Voir avec votre conseiller pro pour plus de détails et d'éventuelles négociations ou rabais selon le volume.
                  </p>
                </div>
              </div>
            )}

            {/* Meuble form */}
            {volumeForm?.articleType === 'meuble' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="LayoutDashboard" size={18} className="text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Enlèvement de meuble(s)</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nombre de meubles</label>
                  <input
                    type="number"
                    min="1"
                    value={volumeForm?.nbMeubles}
                    onChange={(e) => setVolumeForm(prev => ({ ...prev, nbMeubles: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="1"
                  />
                </div>
                <div className="flex items-start space-x-2 bg-white border border-amber-200 rounded-lg p-3">
                  <Icon name="Info" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    Le volume et le tarif des meubles sont calculés sur devis. Notre équipe vous contactera pour évaluer le volume exact.
                  </p>
                </div>
              </div>
            )}

            {/* Palette form */}
            {volumeForm?.articleType === 'palette' && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon name="Layers" size={18} className="text-purple-600" />
                  <span className="text-sm font-semibold text-purple-800">Enlèvement de palette(s)</span>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nombre de palettes</label>
                  <input
                    type="number"
                    min="1"
                    value={volumeForm?.nbPalettes}
                    onChange={(e) => setVolumeForm(prev => ({ ...prev, nbPalettes: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="1"
                  />
                </div>
                <div className="flex items-start space-x-2 bg-white border border-purple-200 rounded-lg p-3">
                  <Icon name="Info" size={16} className="text-purple-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-purple-700">
                    Le volume et le tarif des palettes sont calculés sur devis. Notre équipe vous contactera pour évaluer le volume exact.
                  </p>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description des marchandises</label>
              <textarea value={volumeForm?.description} onChange={(e) => setVolumeForm(prev => ({ ...prev, description: e?.target?.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" rows="3" placeholder="Décrivez le contenu de votre envoi" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Adresse d'enlèvement</label>
              <Input value={volumeForm?.adresseEnlevement} onChange={(e) => setVolumeForm(prev => ({ ...prev, adresseEnlevement: e?.target?.value }))} placeholder="Adresse complète" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date d'enlèvement souhaitée</label>
              <Input type="date" value={volumeForm?.dateEnlevement} onChange={(e) => setVolumeForm(prev => ({ ...prev, dateEnlevement: e?.target?.value }))} />
            </div>

            <div className="flex items-center space-x-2">
              <input type="checkbox" id="volumeEX1" checked={volumeForm?.certificatEX1} onChange={(e) => setVolumeForm(prev => ({ ...prev, certificatEX1: e?.target?.checked }))} className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500" />
              <label htmlFor="volumeEX1" className="text-sm text-gray-700">J'ai besoin du certificat EX1</label>
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Envoi en cours…' : 'Demander un devis'}
            </Button>
          </form>
        )}

        {/* Conteneur Form */}
        {activeOption === 'conteneur' && (
          <form onSubmit={handleConteneurSubmit} className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Réserver un conteneur complet</h2>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type de conteneur</label>
              <select value={conteneurForm?.type} onChange={(e) => setConteneurForm({ ...conteneurForm, type: e?.target?.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500">
                <option value="20pieds">20 pieds</option>
                <option value="40pieds">40 pieds</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description des marchandises</label>
              <textarea value={conteneurForm?.description} onChange={(e) => setConteneurForm({ ...conteneurForm, description: e?.target?.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" rows="3" placeholder="Décrivez le contenu de votre conteneur" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Adresse d'enlèvement</label>
              <Input value={conteneurForm?.adresseEnlevement} onChange={(e) => setConteneurForm({ ...conteneurForm, adresseEnlevement: e?.target?.value })} placeholder="Adresse complète" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date d'enlèvement souhaitée</label>
              <Input type="date" value={conteneurForm?.dateEnlevement} onChange={(e) => setConteneurForm({ ...conteneurForm, dateEnlevement: e?.target?.value })} />
            </div>

            <div className="flex items-center space-x-2">
              <input type="checkbox" id="conteneurEX1" checked={conteneurForm?.certificatEX1} onChange={(e) => setConteneurForm({ ...conteneurForm, certificatEX1: e?.target?.checked })} className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500" />
              <label htmlFor="conteneurEX1" className="text-sm text-gray-700">J'ai besoin du certificat EX1</label>
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? 'Envoi en cours…' : 'Demander un devis'}
            </Button>
          </form>
        )}
      </div>

      {/* Order History */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Historique des commandes</h2>

        {loadingOrders ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : orderHistory?.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Icon name="ShoppingCart" size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Aucune commande pour le moment</p>
            <p className="text-sm mt-1">Vos commandes apparaîtront ici après votre première demande.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Détails</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Montant</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orderHistory?.map((order) => (
                  <tr
                    key={order?.id}
                    className="hover:bg-green-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(order)}
                    title="Cliquer pour voir les détails"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{getOrderTypeLabel(order?.order_type)}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{order?.volume_quantity || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDate(order?.created_at)}</td>
                    <td className="px-4 py-3">{getStatutBadge(order?.status)}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {order?.estimated_amount ? `${parseFloat(order?.estimated_amount)?.toFixed(0)} €` : 'Sur devis'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Détail de la commande</h3>
              <button
                onClick={() => setShowOrderModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-gray-600" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Type</p>
                  <p className="text-sm font-semibold text-gray-900">{getOrderTypeLabel(selectedOrder?.order_type)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Statut</p>
                  {getStatutBadge(selectedOrder?.status)}
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Date de commande</p>
                  <p className="text-sm text-gray-900">{formatDate(selectedOrder?.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Montant estimé</p>
                  <p className="text-sm font-bold text-gray-900">
                    {selectedOrder?.estimated_amount ? `${parseFloat(selectedOrder?.estimated_amount)?.toFixed(0)} €` : 'Sur devis'}
                  </p>
                </div>
              </div>

              {selectedOrder?.volume_quantity && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Détails / Articles</p>
                  <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">{selectedOrder?.volume_quantity}</p>
                </div>
              )}

              {selectedOrder?.pickup_address && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Adresse d'enlèvement</p>
                  <p className="text-sm text-gray-900 flex items-start gap-2">
                    <Icon name="MapPin" size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    {selectedOrder?.pickup_address}
                  </p>
                </div>
              )}

              {selectedOrder?.client_notes && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Notes</p>
                  <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">{selectedOrder?.client_notes}</p>
                </div>
              )}

              {selectedOrder?.needs_ex1 && (
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <Icon name="Award" size={16} className="text-blue-600" />
                  <p className="text-sm text-blue-800 font-medium">Certificat EX1 demandé</p>
                </div>
              )}

              {selectedOrder?.admin_notes && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Note de l'équipe ADT</p>
                  <p className="text-sm text-gray-900 bg-amber-50 border border-amber-200 rounded-lg p-3">{selectedOrder?.admin_notes}</p>
                </div>
              )}

              {selectedOrder?.quote_sent_at && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium mb-1">Devis envoyé le</p>
                  <p className="text-sm text-gray-900">{formatDate(selectedOrder?.quote_sent_at)}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200">
              <Button onClick={() => setShowOrderModal(false)} className="w-full" variant="outline">
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Commander;